import { Injectable, inject, signal, computed, APP_INITIALIZER, Provider, NgZone, DestroyRef, PLATFORM_ID } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { firstValueFrom, catchError, of, retry, timer, filter, take, timeout } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { environment } from '../../../../environments/environment';
import { LoggerService } from '../../logging/services/logger.service';
import { HttpCacheService } from '../../http/services/cache.service';
import { ToastService } from '../../notifications/services/toast.service';
import {
  AuthState,
  AuthResponse,
  AuthScreen,
  LoginRequest,
  OtpStatusResponse,
  RegisterRequest,
  RegisterResponse,
  ResendOtpRequest,
  SocialAccountNotFoundResponse,
  SocialAuthRequest,
  SocialRegisterRequest,
  UserInfo,
  UserRole,
  VerifyOtpRequest,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  ResetPasswordRequest,
  PasswordStrength,
  OAuthChallengeResponse,
} from '../models/auth.models';

export interface SessionResponse {
  isAuthenticated: boolean;
  accessToken: string;
  userId: string;
  email: string;
  role: UserRole;
  firstName?: string;
  lastName?: string;
  username?: string;
  displayName?: string;
  profilePhotoUrl?: string;
  headline?: string;
  expiresAt?: string;
  refreshToken?: string;
  requiresRefresh: boolean;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly logger = inject(LoggerService);
  private readonly httpCache = inject(HttpCacheService);
  private readonly toast = inject(ToastService);
  private readonly ngZone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);
  private readonly apiUrl = environment.apiUrl;
  // App bootstrap must never depend indefinitely on a remote session endpoint. A guest
  // can always use the public/login routes if the API is temporarily unavailable.
  private readonly sessionInitializationTimeoutMs = 5_000;

  private sessionTimer: ReturnType<typeof setTimeout> | null = null;
  private proactiveRefreshTimer: ReturnType<typeof setTimeout> | null = null;
  private isHandlingSessionExpired = false;
  private activeRefreshPromise: Promise<boolean> | null = null;
  private authChannel: BroadcastChannel | null = null;

  constructor() {
    this.setupVisibilityListener();
    this.setupAuthChannel();
  }

  private readonly _state = signal<AuthState>({
    isAuthenticated: false,
    accessToken: null,
    user: null,
    isLoading: true,
    error: null,
    expiresAt: null,
  });

  private readonly _currentScreen = signal<AuthScreen>('landing');
  private readonly _pendingEmail = signal<string | null>(null);
  private readonly _pendingRole = signal<string | null>(null);

  private readonly _socialAccountNotFound = signal<SocialAccountNotFoundResponse | null>(null);

  readonly isAuthenticated = computed(() => this._state().isAuthenticated);
  readonly accessToken = computed(() => this._state().accessToken);
  readonly user = computed(() => this._state().user);
  readonly isLoading = computed(() => this._state().isLoading);
  readonly error = computed(() => this._state().error);
  readonly currentScreen = computed(() => this._currentScreen());
  readonly pendingEmail = computed(() => this._pendingEmail());
  readonly pendingRole = computed(() => this._pendingRole());
  readonly socialAccountNotFound = computed(() => this._socialAccountNotFound());

  async initializeSession(): Promise<void> {
    try {
      const session = await firstValueFrom(
        this.http
          .get<SessionResponse>(`${this.apiUrl}/auth/session`, {
            withCredentials: true,
          })
          .pipe(
            timeout({
              first: this.sessionInitializationTimeoutMs,
              with: () => of(null),
            }),
            catchError((err: HttpErrorResponse) => {
              // 401 means not authenticated - this is expected for guests
              if (err.status === 401) {
                this.logger.debug('User not authenticated (401) - expected for guests');
                return of(null);
              }

              // Network errors (CORS, server unreachable) have status 0
              if (err.status === 0) {
                // Check if it's a CORS error
                if (err.message?.includes('Failed to fetch') ||
                    err.message?.includes('NetworkError') ||
                    err.message?.includes('Network request failed')) {
                  this.logger.warn(
                    'CORS or network error during session init. This may indicate a CORS misconfiguration or the API is unreachable. API URL: ' + this.apiUrl,
                    'AuthService',
                    { error: err }
                  );
                } else {
                  this.logger.warn(
                    `Session initialization failed with network error. API URL: ${this.apiUrl}`,
                    'AuthService',
                    { error: err }
                  );
                }
                return of(null);
              }

              // Other HTTP errors
              this.logger.warn(
                `Session initialization failed: HTTP ${err.status} - ${err.statusText}`,
                'AuthService',
                { url: err.url, error: err.message }
              );
              return of(null);
            }),
          ),
      );

      if (session && session.isAuthenticated) {
        const user: UserInfo = {
          id: session.userId,
          email: session.email,
          role: session.role,
          username: session.username,
          firstName: session.firstName,
          lastName: session.lastName,
          displayName: session.displayName,
          profilePhotoUrl: session.profilePhotoUrl,
          headline: session.headline,
        };

        this._state.set({
          isAuthenticated: true,
          accessToken: session.accessToken,
          user,
          isLoading: false,
          error: null,
          expiresAt: session.expiresAt ?? null,
        });

        if (session.refreshToken && this.isBrowser) {
          try {
            localStorage.setItem('gulu_refresh_token', session.refreshToken);
          } catch {}
        }

        this.scheduleSessionWatchdog(session.expiresAt, session.accessToken);
      } else {
        if (this.isBrowser && localStorage.getItem('gulu_refresh_token')) {
          this.logger.info('Session cookie not recognized on init. Attempting recovery via stored token.', 'AuthService');
          const refreshed = await this.refreshToken();
          if (!refreshed) {
            this._state.update((s) => ({ ...s, isLoading: false }));
          }
        } else {
          this._state.update((s) => ({ ...s, isLoading: false }));
        }
      }
    } catch (err) {
      this.logger.error('Session initialization failed unexpectedly.', 'AuthService', {
        error: err,
      });
      this._state.update((s) => ({
        ...s,
        isLoading: false,
        error: 'Failed to restore session. Please refresh the page.',
      }));
    }
  }

  async waitForSession(timeoutMs = 5000): Promise<void> {
    if (!this._state().isLoading) return;

    try {
      await firstValueFrom(
        toObservable(this._state).pipe(
          filter((s) => !s.isLoading),
          take(1),
          timeout({ first: timeoutMs, meta: 'waitForSession' }),
        ),
      );
    } catch {}
  }

  setPendingRole(role: 'Brand' | 'Creator'): void {
    this._pendingRole.set(role);
    sessionStorage.setItem('gulu_pending_role', role);
  }

  getPendingRole(): 'Brand' | 'Creator' | null {
    const stored = sessionStorage.getItem('gulu_pending_role');
    if (stored === 'Brand' || stored === 'Creator') {
      return stored as 'Brand' | 'Creator';
    }
    const role = this._pendingRole();
    if (role === 'Brand' || role === 'Creator') {
      return role;
    }
    return null;
  }

  clearPendingRole(): void {
    this._pendingRole.set(null);
    sessionStorage.removeItem('gulu_pending_role');
  }

  async register(
    request: RegisterRequest,
    options: { transitionToOtp?: boolean } = {},
  ): Promise<RegisterResponse | null> {
    this._state.update((s) => ({ ...s, isLoading: true, error: null }));

    try {
      const payload: Record<string, string | boolean> = {
        email: request.email,
        password: request.password,
        confirmPassword: request.confirmPassword,
        role: request.role,
        acceptedTerms: request.acceptedTerms,
      };

      if (request.role === 'Brand' && request.companyName) {
        payload['companyName'] = request.companyName;
      } else {
        if (request.firstName) payload['firstName'] = request.firstName;
        if (request.lastName) payload['lastName'] = request.lastName;
      }

      const response = await firstValueFrom(
        this.http.post<RegisterResponse>(`${this.apiUrl}/auth/register`, payload, {
          withCredentials: true,
        }),
      );

      this._pendingEmail.set(request.email);
      this.setPendingRole(request.role);
      if (options.transitionToOtp !== false) {
        this._currentScreen.set('otp-verify');
      }
      this._state.update((s) => ({ ...s, isLoading: false }));

      return response;
    } catch (err) {
      const error = this.handleError(err);
      this._state.update((s) => ({ ...s, isLoading: false, error }));
      return null;
    }
  }

  async verifyOtp(
    request: VerifyOtpRequest,
    options: { navigateAfterSuccess?: boolean } = {},
  ): Promise<AuthResponse | null> {
    this._state.update((s) => ({ ...s, isLoading: true, error: null }));

    try {
      const response = await firstValueFrom(
        this.http.post<AuthResponse>(
          `${this.apiUrl}/auth/verify-otp`,
          {
            email: request.email,
            otpCode: request.otpCode,
          },
          { withCredentials: true },
        ),
      );

      this.setAuthState(response);
      if (options.navigateAfterSuccess !== false) {
        this.navigateByRole(response.role);
      }
      this._state.update((s) => ({ ...s, isLoading: false }));

      return response;
    } catch (err) {
      const error = this.handleError(err);
      this._state.update((s) => ({ ...s, isLoading: false, error }));
      return null;
    }
  }

  async resendOtp(request: ResendOtpRequest): Promise<RegisterResponse | null> {
    this._state.update((s) => ({ ...s, isLoading: true, error: null }));

    try {
      const response = await firstValueFrom(
        this.http.post<RegisterResponse>(`${this.apiUrl}/auth/resend-otp`, {
          email: request.email,
        }),
      );

      this._state.update((s) => ({ ...s, isLoading: false }));
      return response;
    } catch (err) {
      const error = this.handleError(err);
      this._state.update((s) => ({ ...s, isLoading: false, error }));
      return null;
    }
  }

  async login(request: LoginRequest): Promise<AuthResponse | null> {
    this._state.update((s) => ({ ...s, isLoading: true, error: null }));

    try {
      const response = await firstValueFrom(
        this.http.post<AuthResponse>(
          `${this.apiUrl}/auth/login`,
          {
            email: request.email,
            password: request.password,
          },
          { withCredentials: true },
        ),
      );

      this.setAuthState(response);
      this.navigateByRole(response.role);
      this._state.update((s) => ({ ...s, isLoading: false }));

      return response;
    } catch (err) {
      const error = this.handleError(err);
      this._state.update((s) => ({ ...s, isLoading: false, error }));
      return null;
    }
  }

  async logout(): Promise<void> {
    this.clearSessionTimer();
    this.broadcastAuthEvent({ type: 'LOGOUT' });

    try {
      await firstValueFrom(
        this.http.post<void>(`${this.apiUrl}/auth/logout`, {}, { withCredentials: true }),
      );
    } catch {}

    // Clear HTTP cache
    this.httpCache.clear();

    // Clear auth-related storage only (preserve user preferences)
    this.clearAuthStorage();

    // Clear auth state
    this.clearAuthState();
    this.clearPendingRole();
    this._currentScreen.set('landing');

    // Broadcast logout event for services to clean up their signals
    if (this.isBrowser) {
      window.dispatchEvent(new CustomEvent('app:logout'));
    }

    // Navigate using router (SPA navigation, no page reload)
    this.router.navigate(['/']);
  }

  /**
   * Automatically logs out the user when their session completes or expires,
   * cleans up all credentials and state, navigates to /auth/login with a returnUrl,
   * and displays a professional user-facing notification.
   */
  async handleSessionExpired(
    message: string = 'Your session has expired. Please sign in again to continue.',
    broadcast: boolean = true
  ): Promise<void> {
    if (this.isHandlingSessionExpired) {
      return;
    }
    this.isHandlingSessionExpired = true;

    try {
      this.logger.warn('Session expired. Automatically logging out and redirecting to login.', 'AuthService');

      if (broadcast) {
        this.broadcastAuthEvent({ type: 'SESSION_EXPIRED', message });
      }

      // 1. Cancel active session timers
      this.clearSessionTimer();

      // 2. Best-effort server-side logout to clear session cookies
      try {
        await firstValueFrom(
          this.http.post<void>(`${this.apiUrl}/auth/logout`, {}, { withCredentials: true })
        );
      } catch {
        // Ignore network / auth errors during expired session cleanup
      }

      // 3. Clear HTTP cache and client storage
      this.httpCache.clear();
      this.clearAuthStorage();
      this.clearAuthState();
      this.clearPendingRole();
      this._currentScreen.set('landing');

      // 4. Broadcast logout event for all active services (stores, realtime SignalR, chat)
      if (this.isBrowser) {
        window.dispatchEvent(new CustomEvent('app:logout'));
      }

      // 5. Determine returnUrl (do not redirect back to /auth or landing pages)
      const currentUrl = this.router.url;
      const isAuthRoute = currentUrl.startsWith('/auth') || currentUrl === '/';
      const queryParams: Record<string, string> = {
        reason: 'session_expired',
        message
      };
      if (!isAuthRoute) {
        queryParams['returnUrl'] = currentUrl;
      }

      // 6. Present the professional message via Toast
      this.toast.warning(message, { duration: 8000 });

      // 7. Navigate directly to login page inside Angular Zone
      await this.ngZone.run(() => this.router.navigate(['/auth/login'], { queryParams }));
    } finally {
      this.ngZone.runOutsideAngular(() => {
        setTimeout(() => {
          this.isHandlingSessionExpired = false;
        }, 1000);
      });
    }
  }

  private clearSessionTimer(): void {
    if (this.sessionTimer) {
      clearTimeout(this.sessionTimer);
      this.sessionTimer = null;
    }
    if (this.proactiveRefreshTimer) {
      clearTimeout(this.proactiveRefreshTimer);
      this.proactiveRefreshTimer = null;
    }
  }

  private scheduleSessionWatchdog(expiresAtStr: string | null | undefined, token?: string | null): void {
    this.clearSessionTimer();

    let expiryDate: Date | null = null;
    if (expiresAtStr) {
      const parsed = new Date(expiresAtStr);
      if (!isNaN(parsed.getTime())) {
        expiryDate = parsed;
      }
    }

    if (!expiryDate && token) {
      expiryDate = this.parseJwtExp(token);
    }

    if (!expiryDate) {
      return;
    }

    const msRemaining = expiryDate.getTime() - Date.now();

    if (msRemaining <= 0) {
      // Expiry time has already passed
      this.ngZone.run(() => {
        void this.onSessionExpiredReached();
      });
      return;
    }

    // 1. Proactive refresh: attempt silent refresh 1 minute before expiration (or halfway if less than 2 minutes)
    const refreshLeadMs = msRemaining > 120_000 ? 60_000 : Math.floor(msRemaining / 2);
    const refreshInMs = Math.max(1000, msRemaining - refreshLeadMs);

    // Safeguard against 32-bit signed integer overflow in setTimeout (max ~24.8 days)
    const MAX_TIMEOUT_MS = 2_147_483_647;
    const safeRefreshMs = Math.min(refreshInMs, MAX_TIMEOUT_MS);
    const safeSessionMs = Math.min(msRemaining, MAX_TIMEOUT_MS);

    // Run timers completely outside Angular Zone to preserve application stability (isStable)
    // and avoid triggering change detection during idle wait periods
    this.ngZone.runOutsideAngular(() => {
      this.proactiveRefreshTimer = setTimeout(async () => {
        this.logger.debug('Proactively refreshing expiring session token...', 'AuthService');
        const refreshed = await this.refreshToken();
        if (!refreshed) {
          this.logger.warn('Proactive token refresh failed; waiting for hard expiration.', 'AuthService');
        }
      }, safeRefreshMs);

      this.sessionTimer = setTimeout(() => {
        this.ngZone.run(() => {
          void this.onSessionExpiredReached();
        });
      }, safeSessionMs);
    });
  }

  private async onSessionExpiredReached(): Promise<void> {
    this.logger.debug('Session expiry time reached. Attempting final token refresh...', 'AuthService');
    const refreshed = await this.refreshToken();
    if (!refreshed) {
      await this.handleSessionExpired('Your session has expired. Please sign in again to continue.');
    }
  }

  private parseJwtExp(token: string): Date | null {
    try {
      const parts = token.split('.');
      if (parts.length < 2) return null;
      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      const payload = JSON.parse(jsonPayload) as { exp?: number };
      if (typeof payload.exp === 'number') {
        return new Date(payload.exp * 1000);
      }
      return null;
    } catch {
      return null;
    }
  }

  private setupVisibilityListener(): void {
    if (!this.isBrowser) return;

    const onVisibilityOrFocus = () => {
      if (this.isAuthenticated()) {
        void this.checkSessionStatusOnWake();
      }
    };

    // Attach listeners outside Angular Zone so tab focus / switches NEVER trigger change detection passes
    this.ngZone.runOutsideAngular(() => {
      document.addEventListener('visibilitychange', onVisibilityOrFocus);
      window.addEventListener('focus', onVisibilityOrFocus);
      window.addEventListener('online', onVisibilityOrFocus);
    });

    this.destroyRef.onDestroy(() => {
      document.removeEventListener('visibilitychange', onVisibilityOrFocus);
      window.removeEventListener('focus', onVisibilityOrFocus);
      window.removeEventListener('online', onVisibilityOrFocus);
    });
  }

  private setupAuthChannel(): void {
    if (!this.isBrowser || typeof BroadcastChannel === 'undefined') return;

    try {
      this.authChannel = new BroadcastChannel('gulu_auth_sync');
      this.authChannel.onmessage = (event: MessageEvent) => {
        const data = event.data;
        if (!data || typeof data !== 'object') return;

        if (data.type === 'SESSION_EXPIRED') {
          this.ngZone.run(() => {
            void this.handleSessionExpired(data.message, false);
          });
        } else if (data.type === 'LOGOUT') {
          this.ngZone.run(() => {
            this.clearSessionTimer();
            this.httpCache.clear();
            this.clearAuthStorage();
            this.clearAuthState();
            this.clearPendingRole();
            this._currentScreen.set('landing');
            window.dispatchEvent(new CustomEvent('app:logout'));
            void this.router.navigate(['/']);
          });
        } else if (data.type === 'SESSION_REFRESHED' && data.expiresAt) {
          this.ngZone.run(() => {
            if (data.refreshToken && this.isBrowser) {
              try {
                localStorage.setItem('gulu_refresh_token', data.refreshToken);
              } catch {}
            }
            this._state.update((s) => ({
              ...s,
              accessToken: data.accessToken || s.accessToken,
              expiresAt: data.expiresAt,
            }));
            this.scheduleSessionWatchdog(data.expiresAt, data.accessToken);
          });
        }
      };

      this.destroyRef.onDestroy(() => {
        this.authChannel?.close();
        this.authChannel = null;
      });
    } catch {
      // Silently continue if BroadcastChannel is restricted by browser security policies
    }
  }

  private broadcastAuthEvent(message: unknown): void {
    if (this.authChannel) {
      try {
        this.authChannel.postMessage(message);
      } catch {}
    }
  }

  private async checkSessionStatusOnWake(): Promise<void> {
    const expiresAtStr = this._state().expiresAt;
    const token = this._state().accessToken;
    let expiryDate: Date | null = null;
    if (expiresAtStr) {
      const parsed = new Date(expiresAtStr);
      if (!isNaN(parsed.getTime())) expiryDate = parsed;
    }
    if (!expiryDate && token) {
      expiryDate = this.parseJwtExp(token);
    }

    if (expiryDate && expiryDate.getTime() <= Date.now()) {
      if (this.isBrowser && typeof navigator !== 'undefined' && !navigator.onLine) {
        this.logger.warn('Device is offline on tab wake. Deferring token refresh until network reconnect.', 'AuthService');
        return;
      }
      this.logger.warn('Session token expired while tab was inactive/asleep. Attempting background refresh.', 'AuthService');
      const refreshed = await this.refreshToken();
      if (!refreshed && !this._state().isAuthenticated) {
        this.ngZone.run(() => {
          void this.handleSessionExpired('Your session has expired. Please sign in again to continue.');
        });
      }
    }
  }

  private clearAllCachedData(preserveUserPreferences = true): void {
    if (!this.isBrowser) return;

    // 1. Completely purge sessionStorage
    try {
      sessionStorage.clear();
    } catch {}

    // 2. Completely purge localStorage while preserving only intentional, non-sensitive
    // user preferences. Authentication data and every other cached value are removed.
    try {
      const rememberedEmail = localStorage.getItem('gulu_remembered_email');
      const rememberMe = localStorage.getItem('gulu_remember_me');
      const jobPostingTourPreferences = preserveUserPreferences
        ? this.getStoredPreferences('gulu_brand_job_posting_tour_auto_seen:')
        : [];

      localStorage.clear();

      if (rememberedEmail && rememberMe === 'true') {
        localStorage.setItem('gulu_remembered_email', rememberedEmail);
        localStorage.setItem('gulu_remember_me', 'true');
      }

      jobPostingTourPreferences.forEach(([key, value]) => localStorage.setItem(key, value));
    } catch {}

    // 3. Completely purge browser CacheStorage
    if (typeof window !== 'undefined' && 'caches' in window) {
      try {
        window.caches.keys().then((names) => {
          names.forEach((name) => {
            void window.caches.delete(name);
          });
        });
      } catch {}
    }
  }

  private clearAuthStorage(preserveUserPreferences = true): void {
    this.clearAllCachedData(preserveUserPreferences);
  }

  private getStoredPreferences(prefix: string): Array<[string, string]> {
    const preferences: Array<[string, string]> = [];
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (!key?.startsWith(prefix)) continue;
      const value = localStorage.getItem(key);
      if (value !== null) preferences.push([key, value]);
    }
    return preferences;
  }

  async deleteAccount(confirmationText: string): Promise<boolean> {
    this._state.update((s) => ({ ...s, isLoading: true, error: null }));

    try {
      await firstValueFrom(
        this.http.delete<void>(`${this.apiUrl}/account/me`, {
          body: { confirmationText },
          withCredentials: true,
        }),
      );

      // Clear HTTP cache
      this.httpCache.clear();

      // Clear auth-related storage
      this.clearAuthStorage(false);

      // Clear auth state
      this.clearAuthState();
      this.clearPendingRole();
      this._currentScreen.set('landing');

      // Broadcast logout event
      window.dispatchEvent(new CustomEvent('app:logout'));

      // Navigate using router
      this.router.navigate(['/']);
      return true;
    } catch (err) {
      const error = this.handleError(err);
      this._state.update((s) => ({ ...s, isLoading: false, error }));
      return false;
    }
  }

  async refreshToken(): Promise<boolean> {
    if (this.activeRefreshPromise !== null) {
      return this.activeRefreshPromise;
    }

    this.activeRefreshPromise = this.executeRefreshToken().finally(() => {
      this.activeRefreshPromise = null;
    });

    return this.activeRefreshPromise;
  }

  private async executeRefreshToken(): Promise<boolean> {
    try {
      const storedRefreshToken = this.isBrowser ? localStorage.getItem('gulu_refresh_token') : null;
      const response = await firstValueFrom(
        this.http
          .post<AuthResponse>(
            `${this.apiUrl}/auth/refresh`,
            storedRefreshToken ? { refreshToken: storedRefreshToken } : {},
            { withCredentials: true }
          )
          .pipe(
            retry({
              count: 2,
              delay: (error, retryCount) => {
                if (error.status >= 400 && error.status < 500) {
                  return of();
                }
                return timer(Math.pow(2, retryCount) * 250);
              },
            }),
          ),
      );

      if (response?.accessToken) {
        this.setAuthState(response);
        this.broadcastAuthEvent({
          type: 'SESSION_REFRESHED',
          expiresAt: response.expiresAt,
          accessToken: response.accessToken,
          refreshToken: response.refreshToken,
        });
        return true;
      }

      this.clearAuthState();
      return false;
    } catch (err: unknown) {
      if (err instanceof HttpErrorResponse && (err.status === 400 || err.status === 401 || err.status === 403)) {
        this.clearAuthState();
      }
      return false;
    }
  }

  async getOtpStatus(email: string): Promise<OtpStatusResponse | null> {
    try {
      const response = await firstValueFrom(
        this.http.get<OtpStatusResponse>(`${this.apiUrl}/auth/otp-status`, {
          params: { email },
        }),
      );
      return response;
    } catch (err) {
      const error = this.handleError(err);
      this._state.update((s) => ({ ...s, error }));
      return null;
    }
  }

  async socialAuth(request: SocialAuthRequest): Promise<AuthResponse | null> {
    this._state.update((s) => ({ ...s, isLoading: true, error: null }));
    this._socialAccountNotFound.set(null);

    try {
      const response = await firstValueFrom(
        this.http.post<AuthResponse>(
          `${this.apiUrl}/auth/social`,
          {
            provider: request.provider,
            authorizationCode: request.authorizationCode,
            state: request.state,
          },
          { withCredentials: true },
        ),
      );

      this.setAuthState(response);
      this.navigateByRole(response.role);
      this._state.update((s) => ({ ...s, isLoading: false }));

      return response;
    } catch (err) {
      if (err instanceof HttpErrorResponse && err.status === 404) {
        const body = err.error as SocialAccountNotFoundResponse | Record<string, unknown> | null;
        if (
          body &&
          (body as SocialAccountNotFoundResponse).errorCode === 'SOCIAL_ACCOUNT_NOT_FOUND'
        ) {
          const payload = body as SocialAccountNotFoundResponse;
          this._socialAccountNotFound.set(payload);
          this._state.update((s) => ({ ...s, isLoading: false, error: payload.message }));
          return null;
        }
      }

      const error = this.handleError(err);
      this._state.update((s) => ({ ...s, isLoading: false, error }));
      return null;
    }
  }

  async socialRegister(request: SocialRegisterRequest): Promise<AuthResponse | null> {
    this._state.update((s) => ({ ...s, isLoading: true, error: null }));

    try {
      const response = await firstValueFrom(
        this.http.post<AuthResponse>(
          `${this.apiUrl}/auth/social-register`,
          {
            provider: request.provider,
            authorizationCode: request.authorizationCode,
            state: request.state,
            role: request.role,
          },
          { withCredentials: true },
        ),
      );

      this.setAuthState(response);
      this.navigateByRole(response.role);
      this._state.update((s) => ({ ...s, isLoading: false }));

      return response;
    } catch (err) {
      const error = this.handleError(err);
      this._state.update((s) => ({ ...s, isLoading: false, error }));
      return null;
    }
  }

  async forgotPassword(request: ForgotPasswordRequest): Promise<ForgotPasswordResponse | null> {
    this._state.update((s) => ({ ...s, isLoading: true, error: null }));

    try {
      const response = await firstValueFrom(
        this.http.post<ForgotPasswordResponse>(`${this.apiUrl}/auth/forgot-password`, {
          email: request.email,
        }),
      );

      this._state.update((s) => ({ ...s, isLoading: false }));
      return response;
    } catch (err) {
      const error = this.handleError(err);
      this._state.update((s) => ({ ...s, isLoading: false, error }));
      return null;
    }
  }

  async resetPassword(request: ResetPasswordRequest): Promise<boolean> {
    this._state.update((s) => ({ ...s, isLoading: true, error: null }));

    try {
      await firstValueFrom(
        this.http.post<{ message: string }>(`${this.apiUrl}/auth/reset-password`, {
          email: request.email,
          resetCode: request.resetCode,
          newPassword: request.newPassword,
          confirmPassword: request.confirmPassword,
        }),
      );

      this._state.update((s) => ({ ...s, isLoading: false }));
      return true;
    } catch (err) {
      const error = this.handleError(err);
      this._state.update((s) => ({ ...s, isLoading: false, error }));
      return false;
    }
  }

  setScreen(screen: AuthScreen): void {
    this._currentScreen.set(screen);
    this._state.update((s) => ({ ...s, error: null }));
  }

  clearError(): void {
    this._state.update((s) => ({ ...s, error: null }));
  }

  clearSocialAccountNotFound(): void {
    this._socialAccountNotFound.set(null);
  }

  setError(error: string): void {
    this._state.update((s) => ({ ...s, error }));
  }

  setAuthFromResponse(response: AuthResponse): void {
    this.setAuthState(response);
  }

  updateCurrentUserProfilePhoto(profilePhotoUrl: string): void {
    this._state.update((state) => state.user
      ? { ...state, user: { ...state.user, profilePhotoUrl } }
      : state);
  }

  navigateToRoleHome(role: string): void {
    this.navigateByRole(role);
  }

  private setAuthState(response: AuthResponse): void {
    const user: UserInfo = {
      id: response.userId,
      email: response.email,
      role: response.role,
      username: response.username,
      firstName: response.firstName,
      lastName: response.lastName,
      displayName: response.displayName,
      profilePhotoUrl: response.profilePhotoUrl,
      headline: response.headline,
    };

    this._state.set({
      isAuthenticated: true,
      accessToken: response.accessToken,
      user,
      isLoading: false,
      error: null,
      expiresAt: response.expiresAt,
    });

    if (response.refreshToken && this.isBrowser) {
      try {
        localStorage.setItem('gulu_refresh_token', response.refreshToken);
      } catch {}
    }

    this._pendingEmail.set(null);
    this.clearPendingRole();
    this._currentScreen.set('landing');

    this.scheduleSessionWatchdog(response.expiresAt, response.accessToken);
  }

  getAccessToken(): string | null {
    return this._state().accessToken;
  }

  clearAuthState(): void {
    this.clearSessionTimer();
    if (this.isBrowser) {
      try {
        localStorage.removeItem('gulu_refresh_token');
      } catch {}
    }
    this._state.set({
      isAuthenticated: false,
      accessToken: null,
      user: null,
      isLoading: false,
      error: null,
      expiresAt: null,
    });
  }

  private navigateByRole(role: string): void {
    const tree = this.router.parseUrl(this.router.url);
    const returnUrl = tree.queryParams['returnUrl'];
    if (
      returnUrl &&
      typeof returnUrl === 'string' &&
      returnUrl.startsWith('/') &&
      !returnUrl.startsWith('/auth')
    ) {
      void this.router.navigateByUrl(returnUrl);
      return;
    }

    switch (role) {
      case 'Brand':
        this.router.navigate(['/brand/discover']);
        break;
      case 'Creator':
        this.router.navigate(['/creator/listings']);
        break;
      case 'Admin':
        this.router.navigate(['/admin/dashboard']);
        break;
      default:
        this.router.navigate(['/']);
    }
  }

  private handleError(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      let errorData: Record<string, unknown> | string = err.error;

      if (typeof errorData === 'string') {
        const trimmedError = errorData.trim();
        if (trimmedError.startsWith('{')) {
          try {
            errorData = JSON.parse(trimmedError) as Record<string, unknown>;
          } catch {
            if (trimmedError.length > 0 && trimmedError.length < 500) {
              return trimmedError;
            }
            return 'An unexpected error occurred.';
          }
        } else {
          if (trimmedError.length > 0 && trimmedError.length < 500) {
            return trimmedError;
          }
          return 'An unexpected error occurred.';
        }
      }

      const backendMessage =
        ((errorData as Record<string, unknown>)?.['message'] as string) ||
        ((errorData as Record<string, unknown>)?.['error'] as string) ||
        err.message;
      const statusMessage = err.statusText;

      if (err.status === 400) {
        const errors = (errorData as Record<string, unknown>)?.['errors'];
        if (Array.isArray(errors) && errors.length > 0) {
          return errors.join('. ');
        }
        return backendMessage || 'Invalid request. Please check your input.';
      }

      if (err.status === 401) {
        const message =
          ((errorData as Record<string, unknown>)?.['message'] as string) || backendMessage;
        return message || 'Invalid credentials or session expired.';
      }

      if (err.status === 403) {
        return backendMessage || 'You do not have permission to perform this action.';
      }

      if (err.status === 404) {
        return backendMessage || 'The requested resource was not found.';
      }

      if (err.status === 409) {
        return backendMessage || 'An account with this email already exists.';
      }

      if (err.status === 423) {
        return backendMessage || 'Your account is temporarily locked. Please try again later.';
      }

      if (err.status === 429) {
        const retryAfter = (errorData as Record<string, unknown>)?.['retryAfterSeconds'] as
          | number
          | undefined;
        if (retryAfter) {
          const minutes = Math.ceil(retryAfter / 60);
          return `Too many attempts. Please try again in ${minutes} minute(s).`;
        }
        return backendMessage || 'Too many requests. Please try again later.';
      }

      if (err.status === 500) {
        if (backendMessage && backendMessage !== 'An internal server error occurred') {
          return backendMessage;
        }
        return 'An unexpected error occurred. Please try again later.';
      }

      if (backendMessage && backendMessage !== 'An internal server error occurred') {
        return backendMessage;
      }

      return statusMessage || 'An unexpected error occurred.';
    }

    if (err instanceof Error) {
      return err.message;
    }

    if (typeof err === 'object' && err !== null) {
      const errorObj = err as Record<string, unknown>;
      if (errorObj['message']) {
        return errorObj['message'] as string;
      }
      const nestedError = errorObj['error'] as Record<string, unknown> | undefined;
      if (nestedError?.['message']) {
        return nestedError['message'] as string;
      }
    }

    return 'An unexpected error occurred.';
  }

  getLastErrorMessage(): string | null {
    return this._state().error;
  }

  getSocialAccountNotFoundSnapshot(): SocialAccountNotFoundResponse | null {
    return this._socialAccountNotFound();
  }

  async getPasswordStrength(password: string): Promise<PasswordStrength | null> {
    try {
      const response = await firstValueFrom(
        this.http.post<PasswordStrength>(`${this.apiUrl}/auth/password-strength`, {
          password,
        }),
      );
      return {
        score: response.score,
        label: response.label,
        color: response.color,
        suggestions: response.suggestions,
      };
    } catch {
      return null;
    }
  }


  async initiateOAuthLogin(
    provider: string,
    action: 'login' | 'register' = 'login',
    role?: 'Brand' | 'Creator',
  ): Promise<OAuthChallengeResponse | null> {
    try {
      const response = await firstValueFrom(
        this.http.post<OAuthChallengeResponse>(
          `${this.apiUrl}/auth/oauth-challenge`,
          {
            provider,
            action,
            role,
          },
          { withCredentials: true },
        ),
      );

      if (response?.authorizationUrl) {
        window.location.assign(response.authorizationUrl);
      }

      return response;
    } catch (err) {
      const error = this.handleError(err);
      this._state.update((s) => ({ ...s, error }));
      return null;
    }
  }
}

export function initializeAuth(authService: AuthService): () => Promise<void> {
  return () => authService.initializeSession();
}

export const AUTH_INITIALIZER: Provider = {
  provide: APP_INITIALIZER,
  useFactory: initializeAuth,
  deps: [AuthService],
  multi: true,
};
